import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import { InfoOutlined, FileDownloadOutlined, Add } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";
import { localizeDateFull } from "utils/dateFormats";
import { exportExcelFile } from "utils/exportExcelFile";

import {
  useLazyListDriverTransferRequestsQuery,
  useLazyExportDriverTransferRequestsQuery,
} from "store/services/driverTransferRequestApi";

const STATUS_OPTIONS = ["ALL", "pending", "approved", "rejected"];

const STATUS_STYLES = {
  pending: { backgroundColor: "#FEF9E7", textColor: "#D4AC0D" },
  approved: { backgroundColor: "#EBF5FB", textColor: "#1A7A1A" },
  rejected: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
};

const getStatusStyle = (status) =>
  STATUS_STYLES[status?.toLowerCase()] ?? {
    backgroundColor: "#F8F9F9",
    textColor: "#7F8C8D",
  };

const breadcrumbs = [{ title: "Report", path: "/app/crm/reports/list" },
{ title: "Driver Transfer Requests" }];

const DriverTransferRequestListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const [status, setStatus] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [fetchList, { data: listRes, isLoading, isFetching }] =
    useLazyListDriverTransferRequestsQuery();

  const [exportList, { isFetching: isExporting }] =
    useLazyExportDriverTransferRequestsQuery();

  React.useEffect(() => {
    fetchList({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(status !== "ALL" && { status }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    });
  }, [
    currentPage,
    perPage,
    keyword,
    status,
    startDate,
    endDate,
    fetchList,
  ]);

  const handleExport = async () => {
    const res = await exportList({
      ...(keyword && { keyword }),
      ...(status !== "ALL" && { status }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    });

    const rows = res?.data?.data ?? [];
    if (!rows.length) return;

    const sheetData = rows.map((row) => ({
      "From Driver Name": row?.fromDriver?.name ?? "N/A",
      "To Driver Name": row?.toDriver?.name ?? "N/A",
      "From Vehicle Reg": row?.fromVehicle?.registration_number ?? "N/A",
      "To Vehicle Reg": row?.toVehicle?.registration_number ?? "N/A",
      Status: row?.status ?? "N/A",
      "Approved At": row?.approvedAt ? localizeDateFull(row.approvedAt) : "N/A",
      "Rejected At": row?.rejectedAt ? localizeDateFull(row.rejectedAt) : "N/A",
      "Created At": row?.createdAt ? localizeDateFull(row.createdAt) : "N/A",
    }));

    const nameParts = ["Driver Transfer Requests"];
    if (status !== "ALL") nameParts.push(status);
    if (startDate || endDate)
      nameParts.push(`${startDate || "N/A"} to ${endDate || "N/A"}`);
    if (!keyword && status === "ALL" && !startDate && !endDate)
      nameParts.push(new Date().toISOString().slice(0, 10));

    exportExcelFile({ sheetData, fileName: nameParts.join(" - ") });
  };

  const handleClearFilters = () => {
    setStatus("ALL");
    setStartDate("");
    setEndDate("");
  };

  const hasFilters = status !== "ALL" || !!startDate || !!endDate;

  const columns = [
    {
      name: "From Driver",
      selector: (row) => row?.fromDriver?.name,
      cell: (row) => (
        <Link
          to={`/app/crm/driver/${row?.fromDriver?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.fromDriver?.name ? (
            <HighlightedText
              text={row.fromDriver.name}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
          {row?.fromDriver?.driverId && (
            <div className="text-xs text-gray-500 dark:text-gray-300">
              {row.fromDriver.driverId}
            </div>
          )}
        </Link>
      ),
      minWidth: "150px",
    },
    {
      name: "To Driver",
      selector: (row) => row?.toDriver?.name,
      cell: (row) => (
        <Link
          to={`/app/crm/driver/${row?.toDriver?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.toDriver?.name ? (
            <HighlightedText
              text={row.toDriver.name}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
          {row?.toDriver?.driverId && (
            <div className="text-xs text-gray-500 dark:text-gray-300">
              {row.toDriver.driverId}
            </div>
          )}
        </Link>
      ),
      minWidth: "150px",
    },
    {
      name: "From Vehicle",
      cell: (row) => (
        <div>
          <span className="text-sm">
            {row?.fromVehicle?.registration_number || "N/A"}
          </span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.fromVehicle?.vehicle_id || "N/A"}
          </span>
        </div>
      ),
      minWidth: "140px",
    },
    {
      name: "To Vehicle",
      cell: (row) => (
        <div>
          <span className="text-sm">
            {row?.toVehicle?.registration_number || "N/A"}
          </span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.toVehicle?.vehicle_id || "N/A"}
          </span>
        </div>
      ),
      minWidth: "140px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => {
        const { backgroundColor, textColor } = getStatusStyle(row?.status);
        return (
          <Chip
            label={row?.status ?? "N/A"}
            size="small"
            sx={{
              backgroundColor,
              color: textColor,
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          />
        );
      },
      minWidth: "110px",
    },
    {
      name: "Approved At",
      cell: (row) =>
        row?.approvedAt ? (
          <AppTableDateTime date={row.approvedAt} />
        ) : (
          <span className="text-gray-400">—</span>
        ),
      minWidth: "140px",
    },
    {
      name: "Rejected At",
      cell: (row) =>
        row?.rejectedAt ? (
          <AppTableDateTime date={row.rejectedAt} />
        ) : (
          <span className="text-gray-400">—</span>
        ),
      minWidth: "140px",
    },
    {
      name: "Created At",
      selector: (row) => row?.createdAt,
      cell: (row) =>
        row?.createdAt ? (
          <AppTableDateTime date={row.createdAt} />
        ) : (
          "N/A"
        ),
      minWidth: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Link to={`/app/crm/reports/driver-transfer-request/${row._id}/detail`}>
          <IconButton size="small" aria-label="View" color="primary">
            <InfoOutlined />
          </IconButton>
        </Link>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  useAppMeta("Driver Transfer Requests");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={isFetching || isLoading} headerActions={<PageActions />}>
      <BreadcrumbLayout.Paper>
        {/* Filters Row */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {/* Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Range */}
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          {/* Keyword Search */}
          <TableFilter
            searchLabel="Search by driver or vehicle"
            disabled={isLoading || isFetching}
          />

          {/* Clear */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            disabled={!hasFilters}
          >
            Clear
          </Button>

          {/* Export */}
          <LoadingButton
            variant="contained"
            size="small"
            color="success"
            loading={isExporting}
            loadingPosition="start"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
          >
            Export Excel
          </LoadingButton>
        </Box>

        <AppTable
          columns={columns}
          data={listRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={listRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DriverTransferRequestListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/load-transfer/add">
      <Button startIcon={<Add />} variant="contained">
        New Load Transfer
      </Button>
    </Link>
  );
};
